import React, { useState, useEffect } from "react";
import { Button, Input, Label, Spinner } from "reactstrap";
import { CustomSnackbar } from "../../helpers/CustomSnackbar";
import DefaultAvatar from "../../assets/images/avatar.png";

const ProfileImageUploader = ({
  initialImageName,
  onUploadSuccess,
  placeholderImage = DefaultAvatar,
  uploadPath = "/CandidateAttachment",
  assetType = "customers",
}) => {
  const baseUploadUrl =
    "https://sdsjobseekers.runasp.net/uploads/profile_image/";

  const [localFile, setLocalFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(placeholderImage);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 🎯 NEW: Keeps a session lock on the newly saved file string
  const [sessionSavedName, setSessionSavedName] = useState(null);

  useEffect(() => {
    // 🛡️ BLOCKER: If this component just successfully handled an API response,
    // do not let stale or jumping parent properties reset our active display URL
    if (sessionSavedName) {
      console.log(
        "🛡️ [SHIELD ACTIVE] Retaining server verified display filename:",
        sessionSavedName,
      );
      setPreviewUrl(`${baseUploadUrl}${sessionSavedName}?t=${Date.now()}`);
      return;
    }

    if (initialImageName) {
      const computedUrl = initialImageName.startsWith("http")
        ? initialImageName
        : `${baseUploadUrl}${initialImageName}`;

      setPreviewUrl(`${computedUrl}?t=${Date.now()}`);
    } else {
      setPreviewUrl(placeholderImage);
    }
    console.log("============== IMAGE UPLOADER ==============");
    console.log("initialImageName:", initialImageName);
    console.log("sessionSavedName:", sessionSavedName);
    console.log("previewUrl:", previewUrl);
    console.log("===========================================");
  }, [initialImageName, sessionSavedName, placeholderImage]);

  const handleFileChange = (e) => {
    const selectedFile = e?.target?.files[0];
    if (!selectedFile) return;

    const allowedExtensions = ["jpg", "jpeg", "png"];
    const fileExtension = selectedFile.name.split(".").pop().toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      CustomSnackbar.error(
        `Error! Only ${allowedExtensions.join(", ")} formats are allowed.`,
      );
      return;
    }

    if (selectedFile.size > 1500000) {
      CustomSnackbar.error("Error! File size must be less than 1.5 MB.");
      return;
    }

    // Completely clear any previous session save locks so a new upload preview works cleanly
    setSessionSavedName(null);
    setPreviewUrl(URL.createObjectURL(selectedFile));
    setLocalFile(selectedFile);
  };

  const handleUploadSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!localFile) return;

    const payload = new FormData();
    payload.append("file", localFile);
    payload.append("asset_type", assetType);

    setIsSubmitting(true);
    const fullTargetUrl = `https://sdsjobseekers.runasp.net/api${uploadPath}`;

    try {
      const authToken =
        localStorage.getItem("token") ||
        localStorage.getItem("token") ||
        localStorage.getItem("webvi_authToken");

      const requestHeaders = {};
      if (authToken) {
        requestHeaders["Authorization"] = authToken.startsWith("Bearer ")
          ? authToken
          : `Bearer ${authToken}`;
      }

      const response = await fetch(fullTargetUrl, {
        method: "POST",
        headers: requestHeaders,
        body: payload,
      });

      const rawBodyText = await response.text();
      let responseData = JSON.parse(rawBodyText);

      if (responseData && responseData.success) {
        const fileMetadata = responseData?.data;
        const realServerFileName = fileMetadata?.fileName;

        // 🎯 1. First, explicitly save the server's clean .jpg string to our session tracker
        setSessionSavedName(realServerFileName);

        // 🎯 2. Force the preview to point to the real server asset address directly
        const freshServerUrl = `${baseUploadUrl}${realServerFileName}?t=${Date.now()}`;
        setPreviewUrl(freshServerUrl);

        // 3. Clear file selection queue
        setLocalFile(null);

        // 4. Safely bubble up the clean string back to your parent form components
        if (typeof onUploadSuccess === "function") {
          onUploadSuccess(realServerFileName);
        }

        CustomSnackbar.success(
          responseData?.message || "Profile image updated successfully.",
        );
      } else {
        CustomSnackbar.error(responseData?.message || "Upload failed.");
      }
    } catch (err) {
      console.error("Critical upload system exception:", err);
      CustomSnackbar.error("An error occurred during file upload.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="text-center">
      <div
        className="mb-4 profile-user"
        style={{ position: "relative", display: "inline-block" }}
      >
        <img
          src={previewUrl}
          className="rounded-circle img-thumbnail profile-img"
          style={{ width: "120px", height: "120px", objectFit: "cover" }}
          alt="Profile Preview"
          onLoad={() => console.log("✅ Uploader Image Loaded:", previewUrl)}
          onError={(e) => {
            console.log("❌ Uploader Image Failed");
            console.log("Broken URL:", previewUrl);
            console.log("Current src:", e.target.src);
          }}
        />

        {localFile && (
          <Button
            type="button"
            size="sm"
            color="primary"
            className="profile-save-button"
            disabled={isSubmitting}
            onClick={handleUploadSubmit}
            style={{
              position: "absolute",
              top: "50%",
              left: "100%",
              transform: "translateY(-50%)",
              marginLeft: "12px",
              whiteSpace: "nowrap",
              zIndex: 10,
            }}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="me-1" /> Saving
              </>
            ) : (
              "Save Image"
            )}
          </Button>
        )}

        <div className="p-0 rounded-circle profile-photo-edit">
          <Input
            id="profile-img-file-input"
            type="file"
            className="profile-img-file-input"
            onChange={handleFileChange}
            accept="image/*"
            style={{ display: "none" }}
          />
          <Label
            htmlFor="profile-img-file-input"
            className="profile-photo-edit avatar-xs"
            style={{ cursor: "pointer" }}
          >
            <i className="uil uil-edit"></i>
          </Label>
        </div>
      </div>
    </div>
  );
};

export default ProfileImageUploader;
